import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import useSecurity, { loginMethodLabel } from "hooks/companySettings/useSecurity";

function formatDateTime(value?: string) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function Security() {
  const {
    emailMfaRequired,
    loginEvents,
    count,
    page,
    setPage,
    pageSize,
    isLoading,
    isSaving,
    load,
    updateEmailMfaRequired,
  } = useSecurity();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleToggle = (_event: unknown, checked: boolean) => {
    if (checked) {
      setConfirmOpen(true);
      return;
    }
    void updateEmailMfaRequired(false);
  };

  return (
    <div>
      <Typography variant="h4" fontWeight="bold">
        Security
      </Typography>
      {isLoading && loginEvents.length === 0 ? (
        <Typography sx={{ mt: 3 }}>Loading...</Typography>
      ) : (
        <>
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Email verification at sign-in
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                When enabled, every portal user for this company must enter a 6-digit
                code emailed to them after signing in with a password.
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography component="span" variant="body1">
                  Require email verification code at sign-in
                </Typography>
                <Switch
                  checked={emailMfaRequired}
                  onChange={handleToggle}
                  disabled={isSaving}
                  color="primary"
                />
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Login history
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Devices and IP addresses used to sign in to the portal.
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Device</TableCell>
                      <TableCell>IP</TableCell>
                      <TableCell>Method</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loginEvents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography color="text.secondary">No sign-ins recorded yet.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      loginEvents.map((event) => (
                        <TableRow key={event.id}>
                          <TableCell>{formatDateTime(event.createdAt)}</TableCell>
                          <TableCell>
                            {event.userName || "—"}
                            {event.userEmail ? (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {event.userEmail}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>{event.deviceSummary || "Unknown device"}</TableCell>
                          <TableCell>{event.ipAddress || "—"}</TableCell>
                          <TableCell>{loginMethodLabel(event.loginMethod)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={count}
                page={page}
                onPageChange={(_event, nextPage) => {
                  setPage(nextPage);
                  load(nextPage);
                }}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[pageSize]}
              />
            </CardContent>
          </Card>
        </>
      )}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Require email verification?</DialogTitle>
        <DialogContent>
          All portal users for this company will need a verification code on their next
          password sign-in. Existing sessions stay signed in until they expire.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={isSaving}
            onClick={async () => {
              await updateEmailMfaRequired(true);
              setConfirmOpen(false);
            }}
          >
            Enable
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default Security;
